import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import {
  type ControlProps,
  type GroupBase,
  MultiValue,
  type MultiValueGenericProps,
  type OptionProps,
  type Props as SelectProps,
  StylesConfig,
  components as selectComponents,
} from 'react-select';

import { BaseTooltip } from '../BaseTooltip';
import {
  Container,
  ContainerSelect,
  ErrorMessage,
  OptionLabel,
  StyledLabel,
  StyledSelect,
} from './styles';

export type IMultiSelectOptions = {
  value: number;
  label: string;
};

type MultiSelectOptionProps = OptionProps<
  IMultiSelectOptions,
  true,
  GroupBase<IMultiSelectOptions>
>;

const MultiSelectOption = (props: MultiSelectOptionProps): JSX.Element => {
  const message = props.selectProps.getOptionLabel(props.data).trim();
  const content = <OptionLabel>{props.children}</OptionLabel>;
  const optionDataCy = message
    ? `option-${message.toLowerCase()}`
    : undefined;

  return (
    <selectComponents.Option
      {...props}
      innerProps={{
        ...props.innerProps,
        ...(optionDataCy ? { 'data-cy': optionDataCy } : {}),
      }}
    >
      {message ? (
        <BaseTooltip message={message} dataCy="multi-select-option-tooltip">
          {content}
        </BaseTooltip>
      ) : (
        content
      )}
    </selectComponents.Option>
  );
};

type MultiSelectControlProps = ControlProps<
  IMultiSelectOptions,
  true,
  GroupBase<IMultiSelectOptions>
>;

const MultiSelectControl = (props: MultiSelectControlProps): JSX.Element => {
  const controlDataCy = (
    props.selectProps as unknown as Record<string, unknown>
  )['data-cy'] as string | undefined;

  return (
    <selectComponents.Control
      {...props}
      innerProps={{
        ...props.innerProps,
        ...(controlDataCy ? { 'data-cy': controlDataCy } : {}),
      }}
    />
  );
};

type MultiSelectMultiValueLabelProps = MultiValueGenericProps<
  IMultiSelectOptions,
  true,
  GroupBase<IMultiSelectOptions>
>;

const MultiSelectMultiValueLabel = (
  props: MultiSelectMultiValueLabelProps
): JSX.Element => {
  const message = props.selectProps
    .getOptionLabel(props.data as IMultiSelectOptions)
    .trim();
  const content = <OptionLabel>{props.children}</OptionLabel>;

  return (
    <selectComponents.MultiValueLabel {...props}>
      {message ? (
        <BaseTooltip message={message} dataCy="multi-select-value-tooltip">
          {content}
        </BaseTooltip>
      ) : (
        content
      )}
    </selectComponents.MultiValueLabel>
  );
};

export type IMultiSelectProps = {
  options?: IMultiSelectOptions[];
  isClearable?: boolean;
  isSearchable?: boolean;
  required?: boolean;
  label: string;
  onChange?: (selected: IMultiSelectOptions[]) => void;
  onOpenModalChange?: (open: boolean) => void;
  onComponentReady?: () => void;
  value?: IMultiSelectOptions[];
  isLoading?: boolean;
  dataCy?: string;
  errorMessage?: string;
  disabled?: boolean;
  styles?: StylesConfig<IMultiSelectOptions>;
  closeMenuOnSelect?: boolean;
  onMenuScrollToBottom?: () => void;
  onInputChange?: (inputValue: string) => void;
  menuZIndex?: number;
  components?: SelectProps<
    IMultiSelectOptions,
    true,
    GroupBase<IMultiSelectOptions>
  >['components'];
};

export const MultiSelect = forwardRef<HTMLDivElement, IMultiSelectProps>(
  (
    {
      options,
      value,
      isLoading,
      label,
      onChange,
      onOpenModalChange,
      onComponentReady,
      dataCy,
      errorMessage,
      styles,
      disabled,
      closeMenuOnSelect = false,
      onMenuScrollToBottom,
      onInputChange,
      menuZIndex = 9999,
      components: userComponents,
      ...props
    },
    ref
  ) => {
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const [hasValue, setHasValue] = useState((value?.length ?? 0) > 0);

    useEffect(() => {
      onOpenModalChange?.(menuIsOpen);
    }, [menuIsOpen, onOpenModalChange]);

    useEffect(() => {
      if (options && isLoading === false && onComponentReady) {
        onComponentReady();
      }
    }, [options, isLoading, onComponentReady]);

    const handleChange = useCallback(
      (selected: MultiValue<IMultiSelectOptions>) => {
        const selectAllClicked = selected.find((item) => item.value === 0);
        if (selectAllClicked) {
          setMenuIsOpen(false);
        }
        if (selected.length === options?.length) {
          setMenuIsOpen(false);
        }

        setHasValue(selected.length > 0);
        onChange?.([...selected]);
      },
      [onChange, options?.length]
    );

    const shouldLabelActive = menuIsOpen || hasValue;
    const controlDataCy = `dropdown-${label.toLowerCase()}`;

    const selectComponentsMerged = useMemo(
      () => ({
        ...userComponents,
        Option: MultiSelectOption,
        MultiValueLabel: MultiSelectMultiValueLabel,
        Control: MultiSelectControl,
      }),
      [userComponents]
    );

    return (
      <Container ref={ref} data-cy={dataCy}>
        <ContainerSelect>
          <StyledLabel
            data-cy={`label-${label.toLowerCase()}`}
            active={shouldLabelActive}
          >
            {label}
          </StyledLabel>
          <StyledSelect
            {...props}
            options={options}
            placeholder={label}
            isMulti={true}
            value={value}
            menuIsOpen={menuIsOpen}
            onMenuOpen={() => {
              setMenuIsOpen(true);
            }}
            onMenuClose={() => {
              setMenuIsOpen(false);
            }}
            data-cy={controlDataCy}
            isLoading={isLoading}
            onChange={handleChange}
            onMenuScrollToBottom={onMenuScrollToBottom}
            onInputChange={onInputChange}
            menuZIndex={menuZIndex}
            classNamePrefix="select"
            isDisabled={disabled}
            closeMenuOnSelect={closeMenuOnSelect}
            menuPosition="absolute"
            menuPlacement="bottom"
            menuShouldBlockScroll
            menuShouldScrollIntoView
            styles={styles}
            components={selectComponentsMerged}
          />
        </ContainerSelect>

        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </Container>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';
